import { derived, get, writable } from 'svelte/store';
import { Liquid } from 'liquidjs';
import postcss from 'postcss';
import safeParser from 'postcss-safe-parser';

import { BizCardsService } from '@client/services/bizcards';

import {
  colorBackground,
  colorPrimary,
  colorSecondary,
  colorTertiary,
  colorText,
  companyName,
  companyUrl,
  fontBase,
  fontHeadings,
  fontsUrl,
  fontWeightBase,
  fontWeightHeadings,
  iconUrl,
  logoUrl
} from '@client/stores/brand';
import { isBambooHR, isGoogle, isMicrosoft } from '@client/stores/domain-settings';
import { userStore } from '@client/stores/user';

import { generateUniqueRulename } from '@client/utils/rulenames';

import { throttle } from '@common/utils/throttle';

import { hoverBorder } from './bizcard-templates/shared';
import { loadBizcardManifest, loadBizcardTemplate } from './bizcard-templates';

/**
 * @typedef {import('svelte/store').Writable} Writable
 * @typedef {import('svelte/store').Readable} Readable
 * @typedef {import('liquidjs').Template[]} LiquidTemplate
 * @typedef {import('./types').AssignedField} AssignedField
 * @typedef {import('./types').Brand} Brand
 * @typedef {import('./types').Bizcard} Bizcard
 */

const engine = new Liquid();

// Template-related stores

/**
 * The unique identifier (rulename) of the business card.
 * @type {Writable<string>}
 */
export const id = writable('');

/**
 * Tracks updates made to the rulename.
 * @type {Writable<string>}
 */
export const updatedRulename = writable('');

/**
 * A store that holds true if the current template is valid Liquid, otherwise false.
 * @type {Writable<boolean>}
 */
export const isValid = writable(true);

/**
 * A store that holds true while a bizcard or template is being loaded.
 * @type {Writable<boolean>}
 */
export const loading = writable(false);

/**
 * A store containing the JSON string for the web app manifest.
 * @type {Writable<string>}
 */
export const manifest = writable('');

/**
 * A store that holds true while a bizcard is being saved or updated.
 * @type {Writable<boolean>}
 */
export const saving = writable(false);

/**
 * A store containing the raw HTML and Liquid template for the business card.
 * @type {Writable<string>}
 */
export const template = writable('');

// Derived Stores

/**
 * A derived store that automatically extracts all assigned Liquid template variables from the `template` store.
 * Filters out internal variables.
 * @type {Readable<AssignedField[]>}
 */
export const assignedFields = derived(template, ($template) => {
  if (!$template) return [];

  try {
    const parsedTemplate = engine.parse($template);
    const assigned = [];

    for (const token of parsedTemplate) {
      if (token.name !== 'assign' || !token.key || !token.value) {
        continue;
      }

      const name = token.key;
      const expression = token.value;

      const initialPostfix = expression.initial?.postfix?.[0];
      const defaultFilterArg = expression.filters?.[0]?.args?.[0];

      const content = initialPostfix?.props?.[0]?.content;
      const hidden = content === 'undefined';
      const remappedField = content ?? undefined;

      assigned.push({
        name,
        hidden,
        field: remappedField,
        value: initialPostfix?.content,
        default: {
          value: defaultFilterArg?.content,
          field: defaultFilterArg?.props?.[0]?.content
        }
      });
    }

    return assigned;
  } catch (e) {
    console.error(e);
    isValid.set(false);

    return [];
  }
});

/**
 * A derived store that automatically extracts all Liquid template variables from the `template` store.
 * Filters out internal variables.
 * @type {Readable<string[]>}
 */
export const fields = derived(template, ($template) => {
  if (!$template) return [];

  try {
    const parsedTemplate = engine.parse($template);
    const variables = engine.variablesSync(parsedTemplate);
    isValid.set(true);

    const internalVariables = ['image_url', 'smart_url', 'undefined'];
    const namedFields = variables.filter((name) => !name.startsWith('sg') && !internalVariables.includes(name)).sort();

    return namedFields;
  } catch (e) {
    console.error(e);
    isValid.set(false);

    return [];
  }
});

/**
 * A derived store that extracts all CSS custom properties (variables) and their values from the `template` store.
 * @type {Readable<Record<string, string>>}
 */
export const variables = derived(template, ($template, set) => {
  if (!$template) {
    set({});
    return;
  }
  extractCssVariablesWithValues($template).then(set);
});

// Preview-related writable stores

/**
 * A store holding an map of original field names to their new values, remapped variable names, and default values.
 * @type {Writable<Record<string, AssignedField>>}
 */
export const reassignedFields = writable({});

/**
 * A store containing the fully rendered HTML preview of the business card.
 * @type {Writable<string>}
 */
export const renderedTemplate = writable('');

/**
 * The user object used for generating previews.
 * @type {Writable<any>}
 */
export const selectedUser = writable(null);

/**
 * A store that holds the template with CSS variable replacements applied, ready for saving.
 * @type {Writable<string>}
 */
export const updatedTemplate = writable('');

/**
 * A store holding a map of CSS variables that have been changed by the user.
 * @type {Writable<Record<string, string>>}
 */
export const updatedVariables = writable({});

// CSS helper functions

/**
 * Extracts CSS variables from all `<style>` tags within an HTML string.
 * @param {string} html - The HTML content to parse.
 * @returns {Promise<Record<string, string>>} A promise that resolves to a key-value map of CSS variables.
 */
export async function extractCssVariablesWithValues(html) {
  /** @type {Record<string, string>} */
  const cssVariables = {};
  /** @type {Promise<Record<string, string>>[]} */
  const promises = [];
  const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let match;

  while ((match = styleRegex.exec(html)) !== null) {
    const cssContent = match[1];
    promises.push(parseVariables(cssContent));
  }

  const results = await Promise.all(promises);
  for (const vars of results) {
    Object.assign(cssVariables, vars);
  }

  return cssVariables;
}

/**
 * Parses a CSS string to extract CSS variables.
 * @param {string} css - The CSS string to process.
 * @returns {Promise<Record<string, string>>} A promise that resolves to a key-value map of CSS variables.
 */
export async function parseVariables(css) {
  /** @type {Record<string, string>} */
  const cssVariables = {};
  const root = await postcss().process(css, { parser: safeParser });

  root.root.walkDecls((decl) => {
    if (decl.prop.startsWith('--')) {
      cssVariables[decl.prop] = decl.value;
    }
  });

  return cssVariables;
}

/**
 * Replaces a CSS variable's value within an HTML string's style blocks.
 * @param {string} html - The HTML content containing style blocks.
 * @param {string} variable - The full CSS variable name (e.g., '--color-primary').
 * @param {string} newValue - The new value for the variable.
 * @returns {string} The updated HTML string.
 */
export function replaceCssVariable(html, variable, newValue) {
  const safeVariable = escapeRegExp(variable);
  const regex = new RegExp(`(${safeVariable}\\s*:\\s*)([^;]+)(;)`, 'g');
  return html.replace(regex, `$1${newValue}$3`);
}

// Liquid helper functions

/**
 * Escapes characters in a string for safe use inside a Liquid `assign` tag.
 * @param {string} text - The text to escape.
 * @returns {string} The escaped text.
 */
export function escapeLiquidString(text) {
  return text.replace(/["\\]/g, '\\$&');
}

/**
 * Escapes characters in a string for safe use in a Regular Expression.
 * @param {string} text - The text to escape.
 * @returns {string} The escaped text.
 */
export function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validates if a string is a valid Liquid variable name.
 * @param {string} name - The name to validate.
 * @returns {boolean} True if the name is a valid Liquid variable.
 */
export function isValidLiquidVariable(name) {
  const liquidVarRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
  const bracketNotationRegex = /^[a-zA-Z_][a-zA-Z0-9_]*\["[^"\]]+"\]$/;
  return liquidVarRegex.test(name) || bracketNotationRegex.test(name);
}

/**
 * Creates an `assign` tag for a reassigned field.
 * @param {AssignedField} field - The variable to be assigned.
 * @returns {string} The assign tag.
 */
export function createAssignTag(field) {
  if (field.hidden) {
    return `{% assign ${field.name} = undefined %}`;
  }

  let assignment;
  if (field.value) {
    assignment = `"${escapeLiquidString(field.value)}"`;
  } else if (field.field) {
    assignment = field.field;
  } else {
    assignment = field.name;
  }

  // default filter is only when a field, since static values will never be nil.
  if (!field.value && (field.default.field || field.default.value)) {
    const defaultAssignment = field.default.value
      ? `"${escapeLiquidString(field.default.value)}"`
      : field.default.field;
    assignment += ` | default: ${defaultAssignment}`;
  }

  return `{% assign ${field.name} = ${assignment} %}`;
}

/**
 * Updates or adds assign tags in a Liquid template string.
 * @param {string} templateString - The original Liquid template.
 * @param {Record<string, AssignedField>>} desiredMap - Array of assignment objects to apply.
 * @returns {string} The updated Liquid template string.
 */
function reassignLiquidFields(templateString, desiredMap) {
  const tokens = engine.parse(templateString);
  const replacements = [];
  const handledNames = new Set();

  // Find existing assignments to update:
  for (const token of tokens) {
    if (token.name !== 'assign' || !token.key) {
      continue;
    }

    const tokenName = token.key;

    if (desiredMap[tokenName]) {
      const newAssignObj = desiredMap[tokenName];
      const newTagString = createAssignTag(newAssignObj);

      replacements.push({
        start: token.token.begin,
        end: token.token.end,
        content: newTagString
      });
      handledNames.add(tokenName);
    }
  }

  // Sorting reverse ensures character indices remain valid during replacement.
  replacements.sort((a, b) => b.start - a.start);

  let updatedTemplate = templateString;
  for (const rep of replacements) {
    updatedTemplate = updatedTemplate.substring(0, rep.start) + rep.content + updatedTemplate.substring(rep.end);
  }

  // Find and prepend new assignments
  const newTags = [];

  for (const assignObj of Object.values(desiredMap)) {
    if (!handledNames.has(assignObj.name)) {
      newTags.push(createAssignTag(assignObj));
    }
  }

  if (newTags.length > 0) {
    updatedTemplate = newTags.join('\n') + '\n' + updatedTemplate;
  }

  return updatedTemplate;
}

// Actions

/**
 * Resets all stores to their initial, empty state.
 * @returns {void}
 */
export const resetStores = () => {
  id.set('');
  isValid.set(true);
  manifest.set('');
  template.set('');

  reassignedFields.set({});
  renderedTemplate.set('');
  updatedTemplate.set('');
  updatedVariables.set({});
};

/**
 * Loads an existing bizcard from the BizCardsService and populates the stores.
 * @param {string} bizcardId - The ID (rulename) of the bizcard to load.
 * @returns {Promise<void>}
 */
export async function loadBizcard(bizcardId) {
  loading.set(true);
  resetStores();
  id.set('');

  try {
    const response = await BizCardsService.getById(bizcardId);
    id.set(response.rulename);
    template.set(response.htmltemplate);
  } catch (e) {
    console.error(e);
  } finally {
    loading.set(false);
  }
}

/**
 * Gathers current brand settings from various stores into a single Brand object.
 * @returns {Brand} The consolidated brand object.
 */
export const getTemplateDefaults = () => {
  const currentUser = get(userStore);

  const brandDefaults = {
    colorBackground: get(colorBackground) || '#ffffff',
    colorPrimary: get(colorPrimary) || '#2563eb',
    colorSecondary: get(colorSecondary) || '#9ec3ff',
    colorTertiary: get(colorTertiary) || '#a855f7',
    colorText: get(colorText) || '#1f2937',
    companyName: get(companyName),
    companyUrl: get(companyUrl) || currentUser.domain,
    fontBase: get(fontBase) || 'sans-serif',
    fontHeadings: get(fontHeadings) || 'sans-serif',
    fontWeightBase: get(fontWeightBase) || '400',
    fontWeightHeadings: get(fontWeightHeadings) || '700',
    iconUrl: get(iconUrl),
    logoUrl:
      get(logoUrl) ||
      'data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%20160%2040%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20x%3D%221%22%20y%3D%221%22%20width%3D%22158%22%20height%3D%2238%22%20fill%3D%22black%22%20fill-opacity%3D%22.2%22%20stroke%3D%22white%22%20stroke-dasharray%3D%224%22%20%2F%3E%3Ctext%20x%3D%227%22%20y%3D%2225%22%20fill%3D%22white%22%20font-family%3D%22sans-serif%22%3EYOUR%20LOGO%20HERE%3C%2Ftext%3E%3C%2Fsvg%3E',
    fontsUrl: get(fontsUrl)
  };

  return brandDefaults;
};

/**
 * Loads a pre-defined template by name and populates the stores.
 * @param {string} name - The name of the template to load.
 * @returns {Promise<void>}
 */
export async function loadTemplate(name) {
  loading.set(true);
  resetStores();

  try {
    const currentUser = get(userStore);
    const platform = get(isMicrosoft)
      ? 'office365'
      : get(isGoogle)
        ? 'google'
        : get(isBambooHR)
          ? 'bamboohr'
          : undefined;
    const [sgcnameproto, sgcname] = currentUser.cname;
    const smartUrl = `${sgcnameproto}://${sgcname}`;
    const brandDefaults = getTemplateDefaults();
    const imageUrl = `/photo/{{ sgsusername }}/${platform}.png`;

    const tpl = loadBizcardTemplate(name, smartUrl, imageUrl, brandDefaults);
    const defaultManifest = loadBizcardManifest(imageUrl, brandDefaults);

    id.set(generateUniqueRulename(name.toLowerCase()));

    template.set(tpl);
    manifest.set(defaultManifest);
  } catch (e) {
    console.error(e);
  } finally {
    loading.set(false);
  }
}

/**
 * Updates the mapping for a single field.
 * @param {AssignedField} field - The field to reassign.
 * @returns {void}
 */
export function reassignField(field) {
  reassignedFields.update((store) => {
    return { ...store, [field.name]: field };
  });
}

/**
 * Resets the mapping for a single field.
 * @param {string} fieldName - The field name to reassign.
 * @returns {void}
 */
export function resetField(fieldName) {
  reassignedFields.update((store) => {
    const { [fieldName]: _, ...rest } = store;
    return rest;
  });
}

/**
 * Creates a new bizcard by persisting the updated template and settings.
 * @returns {Promise<boolean>} A promise that resolves to true on success, false on failure.
 */
export async function saveBizcard() {
  saving.set(true);

  let tpl = get(updatedTemplate);

  // Replace liquid assign tags using reassignedFields.
  tpl = reassignLiquidFields(tpl, get(reassignedFields));

  // Replace CSS variables using updatedVariables.
  Object.entries(get(updatedVariables)).forEach(([name, value]) => {
    tpl = replaceCssVariable(tpl, name, value);
  });

  try {
    const params = get(fields).map((name) => ({
      name,
      default: '',
      hide: false,
      transform: false
    }));
    const rulename = get(updatedRulename);
    const manifestTemplate = get(manifest);

    const newCard = {
      htmltemplate: tpl,
      manifesttemplate: manifestTemplate,
      params,
      rulename,
      status: false
    };

    await BizCardsService.update(newCard);

    return true;
  } catch (e) {
    console.error(e);

    return false;
  } finally {
    saving.set(false);
  }
}

/**
 * Sets the selected user for preview rendering.
 * @param {any} user - The user object for the preview.
 * @returns {void}
 */
export function setSelectedUser(user) {
  selectedUser.set(user);
}

/**
 * Updates an existing bizcard with the latest template changes.
 * @returns {Promise<boolean>} A promise that resolves to true on success, false on failure.
 */
export async function updateBizcard() {
  saving.set(true);

  let tpl = get(updatedTemplate);

  // Replace liquid assign tags using reassignedFields.
  tpl = reassignLiquidFields(tpl, get(reassignedFields));

  // Replace CSS variables using updatedVariables.
  Object.entries(get(updatedVariables)).forEach(([name, value]) => {
    tpl = replaceCssVariable(tpl, name, value);
  });

  try {
    const currentId = get(id);
    const bizcard = await BizCardsService.getById(currentId);
    const rulename = get(updatedRulename);

    await BizCardsService.update({
      ...bizcard,
      rulename,
      htmltemplate: tpl
    });

    return true;
  } catch (e) {
    console.error(e);

    return false;
  } finally {
    saving.set(false);
  }
}

/**
 * Renders the preview by applying all user changes (remapped fields, updated CSS)
 * to the template and running it through the Liquid engine.
 * @returns {Promise<void>}
 */
export async function updatePreview() {
  const originalTemplate = get(template);

  if (!originalTemplate) {
    renderedTemplate.set('');
    return;
  }

  let tpl = `${originalTemplate}`;

  // Replace CSS variables using updatedVariables.
  Object.entries(get(updatedVariables)).forEach(([name, value]) => {
    tpl = replaceCssVariable(tpl, name, value);
  });

  // Save updated template here with CSS replacements for use in saveBizcard later
  updatedTemplate.set(tpl);

  // Create preview data from selected user and domain
  const currentUser = get(userStore);
  const [sgcnameproto, sgcname] = currentUser.cname;
  const previewUser = get(selectedUser);

  // Update preview data to match reassigned fields
  const reassignedData = Object.entries(get(reassignedFields)).reduce((result, [name, field]) => {
    // Set a preview value that matches the reassignment and/or default:
    let value = field.hidden
      ? undefined
      : previewUser[field.field] ||
        field.value ||
        previewUser[name] ||
        previewUser[field.default?.field] ||
        field.default?.value;

    return {
      ...result,
      [name]: value
    };
  }, {});

  const previewData = {
    ...previewUser,
    ...reassignedData,
    sgcnameproto,
    sgcname,
    sgsusername: previewUser?.username
  };

  // Render updated template with preview data from selected user and domain
  try {
    tpl = `${tpl}\n${hoverBorder.style}\n${hoverBorder.body}`;

    const parsedTemplate = engine.parse(tpl);
    const output = await engine.render(parsedTemplate, previewData);

    renderedTemplate.set(output);
  } catch (e) {
    console.error(e);
    renderedTemplate.set('');
  }
}

/**
 * Directly updates the raw template store with new content.
 * @param {string} newTemplate - The new HTML/Liquid template string.
 * @returns {void}
 */
export function updateTemplate(newTemplate) {
  template.set(newTemplate);
}

/**
 * Updates a single CSS variable's value in the `updatedVariables` store.
 * @param {string} name - The name of the CSS variable.
 * @param {string} value - The new value.
 * @returns {void}
 */
export function updateVariable(name, value) {
  updatedVariables.update((store) => {
    if (value === name) {
      const { [name]: _, ...rest } = store;
      return rest;
    }
    return { ...store, [name]: value };
  });
}

// Subscriptions to update preview automatically
const throttledUpdatePreview = throttle(updatePreview, 75);

reassignedFields.subscribe(throttledUpdatePreview);
selectedUser.subscribe(throttledUpdatePreview);
template.subscribe(throttledUpdatePreview);
updatedVariables.subscribe(throttledUpdatePreview);
